import type React from 'react'
import { CloseIcon, ErrorIcon, InfoIcon } from '../icons'
import type { IconProps } from '../icons/types'
import { cn, cva, type VariantProps } from '../lib/cva'

const toastVariants = cva('flex items-center gap-x-3 w-[365px] rounded-md shadow-md text-left z-100', {
	variants: {
		variant: {
			info: 'bg-neutral-500 text-neutral-50',
			error: 'bg-error-600 text-neutral-50',
		},
		position: {
			topLeft: 'fixed top-6 left-21',
			topRight: 'fixed top-6 right-6',
			bottomLeft: 'fixed bottom-6 left-21',
			bottomRight: 'fixed bottom-6 right-6',
		},
		size: {
			medium: 'py-3 px-4',
			small: 'py-2 px-3 text-sm',
		},
	},
	defaultVariants: {
		variant: 'info',
		position: 'topRight',
		size: 'medium',
	},
})

const iconVariants = cva('', {
	variants: {
		variant: {
			info: 'fill-neutral-50',
			error: 'fill-neutral-50',
		},
	},
	defaultVariants: {
		variant: 'info',
	},
})

interface ToastProps extends VariantProps<typeof toastVariants> {
	icon?: React.FC<IconProps>
	message: string
	onClose?: () => void
	className?: string
}

export const Toast: React.FC<ToastProps> = ({
	variant = 'info',
	position,
	size,
	icon: Icon,
	message,
	onClose,
	className,
}) => {
	const StatusIcon = Icon || (variant === 'error' ? ErrorIcon : InfoIcon)

	return (
		<div className={cn(toastVariants({ variant, position, size }), className)}>
			<div className='flex'>
				<StatusIcon size={size === 'small' ? 18 : 20} className={iconVariants({ variant })} />
			</div>
			<span className='flex-grow'>{message}</span>
			{onClose && (
				<button
					type='button'
					onClick={onClose}
					className='hover:opacity-80 focus:outline-none transition-opacity cursor-pointer'
					aria-label='Close'>
					<CloseIcon size={size === 'small' ? 16 : 20} className={iconVariants({ variant })} />
				</button>
			)}
		</div>
	)
}
